import { IActionContext } from '@msdyn365-commerce/core';

export interface DeliveryDateRange {
    minDeliveryDate: string;
    maxDeliveryDate: string;
}

/**
 * Formats a date to a readable string format like "Day Date Month"
 */
const formatReadableDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'long'
    });
};

/**
 * Checks if a date is an off-day (weekend or public holiday)
 */
const isOffDay = (date: Date, offDayStrings: Set<string>): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6 || offDayStrings.has(date.toDateString());
};

/**
 * Adds business days to a date while skipping off-days
 */
const addBusinessDays = (startDate: Date, daysToAdd: number, offDayStrings: Set<string>): Date => {
    const result = new Date(startDate);
    let addedDays = 0;

    while (addedDays < daysToAdd) {
        result.setDate(result.getDate() + 1);
        if (!isOffDay(result, offDayStrings)) {
            addedDays++;
        }
    }

    return result;
};

/**
 * Calculates delivery date range based on date, context, and selected delivery mode.
 *
 * @param fromDate - Start date (usually current or order date)
 * @param context - D365 Commerce context (contains delivery estimates & holidays)
 * @param deliveryMode - Delivery mode string (e.g., "STD", "HD", "ClickCol")
 * @returns Min, max delivery dates formatted as "Wednesday 14 July" and description
 */
export const getDeliveryDateRange = (fromDate: Date | string, context: IActionContext, deliveryMode: string): DeliveryDateRange | null => {
    const config = context.requestContext.app.config;
    const estimates = config.deliveryEstimatesByMode ?? [];

    const matchedMode = estimates.find((mode: { deliveryMode: string }) => mode.deliveryMode === deliveryMode);

    if (!matchedMode) {
        console.error(`Delivery mode "${deliveryMode}" not found in deliveryEstimatesByMode config`);
        return null;
    }

    const { minDays, maxDays } = matchedMode;
    const storeOffDays = config.storeOffDays ?? [];
    const offDayStrings: Set<string> = new Set(storeOffDays.map((d: string | number | Date) => new Date(d).toDateString()));

    const start = new Date(fromDate);

    const minDate = addBusinessDays(start, minDays, offDayStrings);
    const maxDate = addBusinessDays(start, maxDays, offDayStrings);

    return {
        minDeliveryDate: formatReadableDate(minDate),
        maxDeliveryDate: formatReadableDate(maxDate)
    };
};

export const getDelayedDeliveryMsg = (actionContext: IActionContext, postcode: string) => {
    const delayedDeliveryPostcodes: string[] = actionContext.requestContext.app.config.postcodesWithDelayedDelivery;
    const isDelayedDeliveryPostcode = delayedDeliveryPostcodes?.some(
        delpostcode => postcode.toLowerCase().startsWith(delpostcode.toLowerCase()) || postcode.toLowerCase() === delpostcode.toLowerCase()
    );
    return isDelayedDeliveryPostcode ? actionContext.requestContext.app.config.delayedDeliveryMessage : '';
};
